import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { FaCalendarAlt } from 'react-icons/fa';

const DateOfBirthSelector = ({ onDateChange, darkMode, initialDate, menuPortalTarget }) => {
    const [day, setDay] = useState(null);
    const [month, setMonth] = useState(null);
    const [year, setYear] = useState(null);
    const [daysOptions, setDaysOptions] = useState([]);
    const [monthsOptions, setMonthsOptions] = useState([]);
    const [yearsOptions, setYearsOptions] = useState([]);

    useEffect(() => {
        const currentYear = new Date().getFullYear();
        const minYear = currentYear - 100;
        const maxYear = currentYear - 18;
        setYearsOptions(
            Array.from({ length: maxYear - minYear + 1 }, (_, i) => ({ value: maxYear - i, label: maxYear - i }))
        );
    }, []);

    // Pre-populate when initialDate is provided (e.g. '1990-05-15')
    useEffect(() => {
        if (!initialDate) return;
        const parts = initialDate.split('-').map(Number);
        if (parts.length < 3 || parts.some(isNaN)) return;
        const [y, m, d] = parts;
        setYear({ value: y, label: y });
        setMonth({ value: m, label: m });
        setDay({ value: d, label: d });
    }, [initialDate]);

    useEffect(() => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;
        if (!year) { setMonthsOptions([]); return; }
        let maxMonth = 12;
        if (year.value === currentYear - 18) maxMonth = currentMonth;
        setMonthsOptions(Array.from({ length: maxMonth }, (_, i) => ({ value: i + 1, label: i + 1 })));
        if (month && month.value > maxMonth) setMonth(null);
    }, [year]);

    useEffect(() => {
        if (!month || !year) { setDaysOptions([]); return; }
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;
        const currentDay = currentDate.getDate();
        let maxDay = new Date(year.value, month.value, 0).getDate();
        if (year.value === currentYear - 18 && month.value === currentMonth) maxDay = currentDay;
        setDaysOptions(Array.from({ length: maxDay }, (_, i) => ({ value: i + 1, label: i + 1 })));
        if (day && day.value > maxDay) setDay(null);
    }, [month, year]);

    const handleDayChange = (opt) => {
        setDay(opt);
        if (year && month) onDateChange(`${year.value}-${String(month.value).padStart(2, '0')}-${String(opt.value).padStart(2, '0')}`);
    };
    const handleMonthChange = (opt) => {
        setMonth(opt);
        if (year && day) onDateChange(`${year.value}-${String(opt.value).padStart(2, '0')}-${String(day.value).padStart(2, '0')}`);
        else onDateChange(null);
    };
    const handleYearChange = (opt) => {
        setYear(opt);
        if (month && day) onDateChange(`${opt.value}-${String(month.value).padStart(2, '0')}-${String(day.value).padStart(2, '0')}`);
        else onDateChange(null);
    };

    const customStyles = {
        control: (provided) => ({
            ...provided,
            backgroundColor: darkMode ? '#4a5568' : '#fff',
            color: darkMode ? '#fff' : '#000',
            border: '1px solid transparent',
            boxShadow: 'none',
            '&:hover': { borderColor: darkMode ? '#4a5568' : '#e2e8f0' },
            width: '100%',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? (darkMode ? '#4a5568' : '#e2e8f0')
                : state.isFocused
                    ? (darkMode ? '#4a5568' : '#e2e8f0')
                    : (darkMode ? '#2d3748' : '#fff'),
            color: darkMode ? '#fff' : '#000',
        }),
        singleValue: (provided) => ({ ...provided, color: darkMode ? '#fff' : '#000' }),
        menu: (provided) => ({ ...provided, backgroundColor: darkMode ? '#2d3748' : '#fff', zIndex: 10 }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    };

    const preventKeyboard = (e) => e.preventDefault();
    const portalProps = menuPortalTarget
        ? { menuPortalTarget, menuPosition: 'fixed' }
        : {};

    return (
        <div className={`flex flex-row ${darkMode ? 'bg-gray-700' : 'bg-white'} p-2 rounded-[10px] shadow`}>
            <div className="flex items-center w-full">
                <FaCalendarAlt className={darkMode ? 'text-white' : 'text-gray-500'} />
                <div className="flex-1 min-w-0">
                    <Select value={year} onChange={handleYearChange} options={yearsOptions}
                        styles={{ ...customStyles, input: (p) => ({ ...p, width: '4ch' }) }}
                        placeholder="Año" onKeyDown={preventKeyboard} isSearchable={false} {...portalProps} />
                </div>
                <div className="flex-1 min-w-0">
                    <Select value={month} onChange={handleMonthChange} options={monthsOptions}
                        styles={{ ...customStyles, input: (p) => ({ ...p, width: '2ch' }) }}
                        placeholder="Mes" onKeyDown={preventKeyboard} isDisabled={!year} isSearchable={false} {...portalProps} />
                </div>
                <div className="flex-1 min-w-0">
                    <Select value={day} onChange={handleDayChange} options={daysOptions}
                        styles={{ ...customStyles, input: (p) => ({ ...p, width: '2ch' }) }}
                        placeholder="Día" onKeyDown={preventKeyboard} isDisabled={!month} isSearchable={false} {...portalProps} />
                </div>
            </div>
        </div>
    );
};

export default DateOfBirthSelector;
